import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import App from "./App.vue";
import router from "./router";
import store from "./store/index";

import "./index.css";
import { fontFaces } from "./assets/ts/fonts";
import de from "./locales/de.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import nl from "./locales/nl.json";
import ko from "./locales/ko.json";
import fr from "./locales/fr.json";
import zhtw from "./locales/zh-TW.json";
import ja from "./locales/ja.json";
import no from "./locales/no.json";

// Inject fonts into the document head to ensure they load correctly even when the app is inside a Shadow DOM.
if (!document.getElementById("rubin-fonts")) {
  const style = document.createElement("style");
  style.id = "rubin-fonts";
  style.textContent = fontFaces;
  document.head.appendChild(style);
}

const countryFormats: Record<string, any> = {
  DE: {
    currency: {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
  US: {
    currency: {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
  ES: {
    currency: {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
  NL: {
    currency: {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
  KR: {
    currency: {
      style: "currency",
      currency: "KRW",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
  FR: {
    currency: {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
  TW: {
    currency: {
      style: "currency",
      currency: "TWD",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
  JP: {
    currency: {
      style: "currency",
      currency: "JPY",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
  NO: {
    currency: {
      style: "currency",
      currency: "NOK",
      maximumFractionDigits: 0,
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: "percent",
      useGrouping: false,
    },
  },
};

const languages = ["de", "en", "es", "nl", "ko", "fr", "zh", "ja", "no"];
const countries = ["DE", "US", "ES", "NL", "KR", "FR", "TW", "JP", "NO"];

const numberFormats: Record<string, any> = {};
languages.forEach((lang) => {
  countries.forEach((country) => {
    numberFormats[`${lang}-${country}`] = countryFormats[country];
  });
});

type SupportedLanguage = "de" | "en" | "es" | "nl" | "ko" | "fr" | "zh-TW" | "ja" | "no";
type SupportedCountry = "DE" | "US" | "ES" | "NL" | "KR" | "FR" | "TW" | "JP" | "NO";
type SupportedLocale = `${SupportedLanguage}-${SupportedCountry}`;

const messages: Record<string, any> = {
  de,
  en,
  es,
  nl,
  ko,
  fr,
  zh: zhtw,
  ja,
  no,
};

function mapToSupportedLocale(code: string | undefined | null): SupportedLocale {
  if (!code) return "de-DE";
  // If the code is already a full locale like "en-DE", return it as is if it follows the pattern
  if (code.includes("-")) {
    let [lang, country] = code.split("-");

    if (code.split("-").length == 3) {
      lang = code.split("-").splice(0, 2).join("-");
      country = code.split("-").splice(2, 1).join("-");
    }

    const supportedLangs = ["de", "en", "es", "nl", "ko", "fr", "zh-TW", "ja", "no"];
    const supportedCountries = ["DE", "US", "ES", "NL", "KR", "FR", "TW", "JP", "NO"];
    
    let resolvedLang = (supportedLangs.find(l => lang.toLowerCase().startsWith(l)) || "de") as SupportedLanguage;
    const resolvedCountry = (supportedCountries.find(c => country.toUpperCase() === c) || "DE") as SupportedCountry;
    
    // special case for zh-TW
    if (lang.toLowerCase().startsWith("zh")) resolvedLang = "zh-TW";

    return `${resolvedLang}-${resolvedCountry}` as SupportedLocale;
  }

  const lc = code.toLowerCase();
  if (lc.startsWith("de")) return "de-DE";
  if (lc.startsWith("en")) return "en-US";
  if (lc.startsWith("es")) return "es-ES";
  if (lc.startsWith("nl")) return "nl-NL";
  if (lc.startsWith("ko")) return "ko-KR";
  if (lc.startsWith("fr")) return "fr-FR";
  if (lc.startsWith("zh")) return "zh-TW-TW";
  if (lc.startsWith("ja")) return "ja-JP";
  if (lc.startsWith("no")) return "no-NO";
  return "de-DE";
}

// Resolve initial locale with priority: boot props (window.__CONFIGURATOR_PROPS__.language) → persisted → browser
const persisted = localStorage.getItem("locale") as SupportedLocale | null;
const bootLang: string | undefined = window.__CONFIGURATOR_PROPS__?.language;
const bootCountry: string | undefined = window.__CONFIGURATOR_PROPS__?.country;
const browser = typeof navigator !== "undefined" ? navigator.language : "de-DE";

let initialLocale: SupportedLocale;
if (bootLang && bootCountry) {
  initialLocale = mapToSupportedLocale(`${bootLang}-${bootCountry}`);
} else if (bootLang) {
  initialLocale = mapToSupportedLocale(bootLang);
} else if (persisted) {
  initialLocale = persisted as SupportedLocale;
} else {
  initialLocale = mapToSupportedLocale(browser);
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: initialLocale,
  fallbackLocale: "de",
  messages,
  numberFormats,
});

createApp(App).use(store).use(router).use(i18n).mount("#rubin-items");
